import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { TClass, TModelDefaultsPreview } from '@schemas'
import { apiClient } from '@/lib/apiClient'
import { Button } from '@/components/ui/button'
import RulesViewer from './RulesViewer'
import { mergeCatalogRules, safeJsonParse } from '@/lib/catalog'
import { resolveLabelsPerExhibitFromClass } from '@/lib/labels-per-exhibit'

export function ModelDefaultsReview({
  klass,
  showId,
  enabled,
}: {
  klass: TClass
  showId: string
  enabled: boolean
}) {
  const qc = useQueryClient()
  const [review, setReview] = useState<TModelDefaultsPreview | null>(null)
  const [message, setMessage] = useState('')
  const preview = useMutation({
    mutationFn: () => apiClient.previewClassModelDefaults(klass.id!),
    onSuccess: result => {
      setReview(result)
      setMessage('')
    },
    onError: error => {
      setReview(null)
      setMessage(error.message)
    },
  })
  const apply = useMutation({
    mutationFn: () => apiClient.applyClassModelDefaults(klass.id!, review!.reviewToken),
    onSuccess: () => {
      setReview(null)
      setMessage('Model defaults updated. Show-specific settings were preserved.')
      qc.invalidateQueries({ queryKey: ['divisions', showId] })
      qc.invalidateQueries({ queryKey: ['shows'] })
      qc.invalidateQueries({ queryKey: ['show', 'stats'] })
      qc.invalidateQueries({ queryKey: ['show-series'] })
    },
    onError: error => {
      setReview(null)
      setMessage(`${error.message} Request a new review before applying.`)
    },
  })
  const savedSettings = review
    ? Object.fromEntries(
        Object.entries(review.preserved).map(([key, value]) => [
          key,
          typeof value === 'string' && ['parameters', 'ruleOverrides'].includes(key)
            ? safeJsonParse(value, value)
            : value,
        ])
      )
    : klass
  const pending = preview.isPending || apply.isPending
  return (
    <section className="space-y-3 rounded-lg border p-4" aria-label="Model defaults">
      <h3 className="font-semibold">Model defaults</h3>
      <p className="text-sm text-muted-foreground">
        {klass.modelDefaults?.kind === 'adopted'
          ? `Adopted ${klass.modelDefaults.adoptedAt?.slice(0, 10)}. Model revision ${klass.modelDefaults.model?.revision}.`
          : 'Legacy baseline: original adoption date and version are unknown.'}
      </p>
      {!enabled && (
        <p className="text-sm">Updating defaults requires class-edit access and a draft show.</p>
      )}
      <Button
        type="button"
        variant="outline"
        disabled={!enabled || !klass.catalogClassId || pending}
        onClick={() => preview.mutate()}
      >
        Review model updates
      </Button>
      {message && <p role="status">{message}</p>}
      {review && (
        <div className="space-y-3">
          <p>
            Your saved class name, code, rules, overrides, parameters, entry limits, and award
            settings will be preserved. Unsaved edits are not included in this review. This updates
            only the adopted model defaults.
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            {(
              [
                ['Current', review.current],
                ['Proposed', review.proposed],
              ] as const
            ).map(([label, snapshot]) => (
              <div key={label}>
                <h4 className="font-semibold">{label}</h4>
                <p>
                  {snapshot?.defaults?.name}
                  {snapshot?.subtype ? ` — ${snapshot.subtype.name}` : ''}
                </p>
                <p>
                  Category: {snapshot?.defaults?.categoryCode ?? 'None'} —{' '}
                  {snapshot?.defaults?.categoryName}
                </p>
                <p>{snapshot?.defaults?.description}</p>
                <p>
                  Labels per exhibit:{' '}
                  {resolveLabelsPerExhibitFromClass({
                    ...savedSettings,
                    catalogClass: snapshot?.defaults,
                  })}
                </p>
                <p className="font-medium">Flags</p>
                <RulesViewer rules={snapshot?.defaults?.flags ?? {}} />
                <p className="font-medium">Default rules</p>
                <RulesViewer rules={snapshot?.defaults?.rules ?? {}} />
                <p className="font-medium">Effective rules with saved overrides</p>
                <RulesViewer
                  rules={mergeCatalogRules(
                    snapshot?.defaults?.rules ?? klass.rules ?? {},
                    savedSettings.ruleOverrides ?? {}
                  )}
                />
              </div>
            ))}
          </div>
          <Button type="button" disabled={!enabled || pending} onClick={() => apply.mutate()}>
            Confirm adoption of these defaults
          </Button>
          <Button type="button" variant="ghost" disabled={pending} onClick={() => setReview(null)}>
            Cancel
          </Button>
        </div>
      )}
    </section>
  )
}
